/**
 * PlaceholderTag — art-debt tagging for visual meshes/props.
 *
 * The Cats of Chronos codebase mixes finished art (glTF environment models,
 * cat models, interactive props) with procedural placeholder geometry built
 * from `MeshBuilder.CreateBox/CreateTorus/CreateCylinder/...`. Without a
 * tagging system, art debt is invisible — there's no way to query "what
 * placeholders does Level 3 still have?" or to assert "this level has been
 * art-passed and must contain zero placeholder tags".
 *
 * This module provides:
 *   1. The `PlaceholderTag` type — `final | modular-final | placeholder`.
 *   2. `tagAs(node, tag)` / `getTag(node)` — set/read a tag on any
 *      `AbstractMesh` or `TransformNode` via the node's `metadata` field.
 *   3. `collectTagsRecursive(root)` — walk a subtree and bucket descendants
 *      by their tag (untagged meshes go in their own bucket).
 *   4. `auditSceneTags(scene)` — count tagged meshes across the entire
 *      scene; untagged meshes are treated as implicit placeholders for the
 *      audit summary.
 *
 * Usage in scene/puzzle code:
 * ```ts
 * import { tagAs } from "../levels";
 *
 * const gear = MeshBuilder.CreateTorus("gear", { ... }, scene);
 * tagAs(gear, "placeholder");  // procedural geometry, art-debt
 *
 * const envInstance = await getAssetManager().instantiateModel(scene, "/atrium_env.glb");
 * tagAs(envInstance.rootNodes[0], "final");  // shipped art
 * ```
 *
 * Tags do not affect rendering or gameplay — they're purely informational
 * metadata that tests and dev tools can introspect.
 */
import type { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import type { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import type { Scene } from "@babylonjs/core/scene";

/**
 * Art-debt tag for visual meshes/props.
 *
 * - `final`: production art, glTF model from `assets/models/`, passes visual
 *   review. These should ship in any level whose `artStatus` is `"final"`.
 * - `modular-final`: composed from modular kits (preset lights, shared
 *   materials, reusable cue rigs, etc.) that together look production-ready,
 *   but the source is procedural rather than authored in Blender. Treated as
 *   shippable for art-pass purposes.
 * - `placeholder`: temporary procedural geometry (`MeshBuilder.*`) used while
 *   final art is being authored. These should NOT ship in `"final"` levels.
 */
export type PlaceholderTag = "final" | "modular-final" | "placeholder";

/**
 * Metadata key used on Babylon.js mesh/node `metadata` to store the tag.
 *
 * Set via:
 * ```ts
 * mesh.metadata = { ...mesh.metadata, [PLACEHOLDER_TAG_KEY]: "final" };
 * ```
 *
 * Or, more conveniently, via {@link tagAs}.
 */
export const PLACEHOLDER_TAG_KEY = "placeholderTag" as const;

/**
 * Tag a mesh (or `TransformNode` subtree root) with an art-debt label.
 *
 * Merges into existing `metadata` rather than replacing it, so other systems
 * (e.g. interactive references, NPC type tags) can coexist on the same node.
 *
 * Tags are only attached to the node passed in. To tag every descendant of a
 * subtree (e.g. an instantiated glTF model), call this function in a loop
 * over `root.getChildMeshes()` — or just tag the root and rely on
 * {@link collectTagsRecursive} to surface "untagged" descendants for audits.
 */
export function tagAs(
  node: AbstractMesh | TransformNode,
  tag: PlaceholderTag,
): void {
  const existing = (node.metadata ?? {}) as Record<string, unknown>;
  node.metadata = { ...existing, [PLACEHOLDER_TAG_KEY]: tag };
}

/**
 * Read the placeholder tag from a node, or `undefined` if the node is not
 * tagged. Validates the value is one of the known {@link PlaceholderTag}
 * literals so a malformed metadata entry isn't silently treated as valid.
 */
export function getTag(
  node: AbstractMesh | TransformNode,
): PlaceholderTag | undefined {
  const meta = node.metadata as Record<string, unknown> | null | undefined;
  const tag = meta?.[PLACEHOLDER_TAG_KEY];
  if (tag === "final" || tag === "modular-final" || tag === "placeholder") {
    return tag;
  }
  return undefined;
}

/**
 * Bucket of meshes returned by {@link collectTagsRecursive}.
 *
 * Note: `modularFinal` is camelCase here even though the tag literal is
 * `"modular-final"`, to match TypeScript object-key conventions and keep
 * downstream consumers from having to quote bracketed accesses.
 */
export interface TagBuckets {
  final: AbstractMesh[];
  modularFinal: AbstractMesh[];
  placeholder: AbstractMesh[];
  /** Meshes with no recognized tag — implicit placeholders for audit purposes. */
  untagged: AbstractMesh[];
}

/**
 * Walk a `TransformNode` (or `AbstractMesh`) subtree and bucket every
 * descendant child mesh by its placeholder tag.
 *
 * Uses Babylon's `getChildMeshes(false)` (recursive) to find every mesh
 * underneath the root, regardless of intermediate `TransformNode` layers.
 * The root itself is not included in the result — only its descendant
 * meshes — because the root is usually an invisible container.
 *
 * For full-scene audits, prefer {@link auditSceneTags}.
 */
export function collectTagsRecursive(
  root: TransformNode | AbstractMesh,
): TagBuckets {
  const buckets: TagBuckets = {
    final: [],
    modularFinal: [],
    placeholder: [],
    untagged: [],
  };

  // `false` = recursive (descendants of all depths), default filter (all meshes).
  const meshes = root.getChildMeshes(false);
  for (const mesh of meshes) {
    const tag = getTag(mesh);
    switch (tag) {
      case "final":
        buckets.final.push(mesh);
        break;
      case "modular-final":
        buckets.modularFinal.push(mesh);
        break;
      case "placeholder":
        buckets.placeholder.push(mesh);
        break;
      default:
        buckets.untagged.push(mesh);
        break;
    }
  }

  return buckets;
}

/**
 * Summary counts returned by {@link auditSceneTags}.
 */
export interface SceneTagAudit {
  /** Number of meshes tagged `"final"`. */
  final: number;
  /** Number of meshes tagged `"modular-final"`. */
  modularFinal: number;
  /** Number of meshes tagged `"placeholder"`. */
  placeholder: number;
  /** Number of meshes with no recognized tag (treated as implicit placeholders). */
  untagged: number;
  /** Total number of meshes in the scene (sum of the four buckets above). */
  totalMeshes: number;
}

/**
 * Audit every mesh in a scene by tag, returning a summary count.
 *
 * Untagged meshes are reported separately so callers can distinguish
 * "explicitly marked as placeholder" from "not yet considered for tagging".
 * For art-gate tests, both `placeholder` and `untagged` typically count
 * against a level whose `artStatus` is `"final"`.
 */
export function auditSceneTags(scene: Scene): SceneTagAudit {
  const audit: SceneTagAudit = {
    final: 0,
    modularFinal: 0,
    placeholder: 0,
    untagged: 0,
    totalMeshes: 0,
  };

  for (const mesh of scene.meshes) {
    audit.totalMeshes++;
    const tag = getTag(mesh);
    switch (tag) {
      case "final":
        audit.final++;
        break;
      case "modular-final":
        audit.modularFinal++;
        break;
      case "placeholder":
        audit.placeholder++;
        break;
      default:
        audit.untagged++;
        break;
    }
  }

  return audit;
}
